import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import Button from 'components/ds/Button'
import { Checkbox } from 'components/ds/Choice'
import { Modal } from 'components/ds/Feedback'
import { Toast } from 'components/shared/Toaster'
import { getApplicationShares, shareApplications, unshareApplication } from 'actions/applicationShares'

const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--border-default)' }
const mutedStyle = { color: 'var(--fg-secondary)', fontSize: 13 }
const textStyle = { color: 'var(--fg-secondary)', fontSize: 14 }

/**
 * Shares the application with its assigned sales representative's login (they get an email). Anyone who
 * already has access is listed and can be removed.
 */
const ShareWithSalesRepsModal = ({ open, onClose, applicationId, businessName }) => {
    // { salesRepresentativeName, salesRepresentativeEmail, salesRepresentativeHasLogin, users }
    const [data, setData] = useState(null)
    const reps = data?.users || []
    const [loading, setLoading] = useState(false)
    const [selected, setSelected] = useState([])
    const [saving, setSaving] = useState(false)
    // Only the latest request may update the dialog: one for another application, or from before it was closed,
    // must not show its users here (Share and Remove would then act on the wrong application)
    const requestId = useRef(0)

    const load = useCallback(() => {
        const id = ++requestId.current
        setData(null)
        setLoading(true)
        getApplicationShares(applicationId)
            .then(res => { if (id === requestId.current) setData(res || null) })
            .catch(() => { if (id === requestId.current) setData(null) })
            .finally(() => { if (id === requestId.current) setLoading(false) })
    }, [applicationId])

    useEffect(() => {
        if (!open) return undefined
        setSelected([])
        load()
        // Closing, or switching application, drops any request still on its way (bumping the live counter is the point)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => { requestId.current++ }
    }, [open, load])

    const toggle = (userId) => setSelected(prev =>
        prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId])

    const share = () => {
        setSaving(true)
        shareApplications([applicationId], selected)
            .then(() => {
                Toast({ type: 'success', message: selected.length === 1 ? 'Shared with 1 sales rep' : `Shared with ${selected.length} sales reps` })
                onClose()
            })
            .catch(() => {})
            .finally(() => setSaving(false))
    }

    const remove = (rep) => {
        unshareApplication(applicationId, rep.userId)
            .then(() => {
                Toast({ type: 'success', message: `${rep.name} no longer has access` })
                load()
            })
            .catch(() => {})
    }

    const renderRep = (rep) => (
        <div key={rep.userId} style={rowStyle}>
            {rep.sharedAt
                ? <div>
                    <div>{rep.name}</div>
                    <div style={mutedStyle}>
                        {rep.email} · Has access since {moment(rep.sharedAt).format('MMM D, YYYY')}{rep.sharedBy ? `, shared by ${rep.sharedBy}` : ''}
                    </div>
                </div>
                : <Checkbox
                    label={<span>{rep.name} <span style={mutedStyle}>{rep.email}</span></span>}
                    checked={selected.includes(rep.userId)}
                    onChange={() => toggle(rep.userId)}
                />
            }
            {rep.sharedAt &&
                <Button variant='ghost' size='small' onClick={() => remove(rep)}>Remove</Button>
            }
        </div>
    )

    // Only the assigned sales representative's login can be given access
    const canShare = !!data?.salesRepresentativeHasLogin

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={businessName ? `Share ${businessName}` : 'Share application'}
            footer={(
                <>
                    <Button variant='secondary' size='small' onClick={onClose}>Close</Button>
                    {canShare &&
                        <Button variant='primary' size='small' loading={saving} disabled={!selected.length} onClick={share}>
                            Share
                        </Button>
                    }
                </>
            )}
        >
            {loading && <p style={textStyle}>Loading...</p>}
            {!loading && data && !data.salesRepresentativeName &&
                <p style={textStyle}>
                    This application has no sales representative. Assign one in Business Information (Edit) to share it with them.
                </p>
            }
            {!loading && data?.salesRepresentativeName && !canShare &&
                <p style={textStyle}>
                    {data.salesRepresentativeName} is this application's sales representative but has no login yet.
                    Create one in Settings &gt; Sales Representative to share it with them.
                </p>
            }
            {!loading && canShare &&
                <p style={textStyle}>
                    Sales reps see only the application details and attachments, and get an email when you share it.
                </p>
            }
            {!loading && reps.map(renderRep)}
        </Modal>
    )
}

export default ShareWithSalesRepsModal
